<template>
  <master-layout :enable-scroll-event="true" id="updatesOverview">
    <div class="flex flex-col w-full min-h-screen bg-[#EAE8E5] pb-[64px]">
      <!-- Full-screen loading overlay -->
      <div
        v-if="isLoading"
        class="p-[16px] fixed top-0 start-0 w-full h-full bg-[#EAE8E5]/90 z-1000 flex items-center justify-center"
      >
        <LoadingSpinner />
      </div>
      <BackButton class="w-full px-5 md:px-6 lg:px-8 mb-[16px]" />
      <div
        class="text-[2rem] leading-[2.5rem] text-[#433D31] font-black mx-5 md:mx-6 lg:mx-8 mb-6 tracking-[-0.03em]"
      >
        Notifications
      </div>
      <BaseSwiper
        id="update-type-swiper"
        ref="update-type-swiper"
        v-slot="slotProps"
        :items="updateTypes"
        :options="swiperOptions"
        class="mt-1 mx-5 md:mx-6 lg:mx-8 mb-6"
      >
        <BaseButton
          class="button border-4 border-solid border-[#3C3A36] rounded-full object-center hover:bg-[#3C3A36] hover:border-[#3C3A36] hover:!text-white whitespace-nowrap"
          :class="activeStyle(slotProps.item.value)"
          :style="{
            'font-size': '16px',
            'padding-left': '12px',
            'padding-right': '12px',
          }"
          @click="filterUpdates(slotProps.item.value)"
          >{{ slotProps.item.name }} {{ getUnreadCountDisplay(slotProps.item.value) }}</BaseButton
        >
      </BaseSwiper>

      <section>
        <div v-if="todaysUpdates.length > 0">
          <div
            class="text-[1rem] leading-[1.25rem] mb-[4px] mx-5 md:mx-6 lg:mx-8 font-bold text-[#3C3A36] md:mb-[32px]"
          >
            Today
          </div>
          <UpdatesCard
            v-for="update in todaysUpdates"
            :id="`card${update.id}`"
            :key="update.id"
            :update="update"
            :status="update.isNew"
            class="scroll-margin cursor-pointer"
            @navigate="navigate"
          ></UpdatesCard>
        </div>
        <div v-if="earlierUpdates.length > 0">
          <div
            class="text-[1rem] leading-[1.25rem] mb-[4px] mx-5 md:mx-6 lg:mx-8 font-bold text-[#3C3A36] md:mb-[32px]"
          >
            Earlier
          </div>
          <UpdatesCard
            v-for="update in earlierUpdates"
            :id="`card${update.id}`"
            :key="update.id"
            :update="update"
            :status="update.isNew"
            class="scroll-margin cursor-pointer"
            @navigate="navigate"
          ></UpdatesCard>
        </div>
        <div v-if="updates.length === 0">
          <div
            class="text-[1rem] leading-[1.25rem] pt-[124px] mb-[4px] text-center font-bold text-[#3C3A36] md:mb-[32px]"
          >
            Nothing here yet 📭
          </div>
        </div>
      </section>
      <div v-if="updates.length > 0" class="my-8 md:my-10 lg:my-12">
        <div class="flex flex-col cursor-pointer" @click="backToTop()">
          <div class="flex flex-col flex-wrap justfiy-center mb-[24px] space-y-2">
            <img src="/assets/icons/back-to-top-icon-blue.svg" />
            <p
              class="text-center text-[1rem] leading-[1.5rem] font-aktiv-grotesk-trial hover:underline"
            >
              Back to top
            </p>
          </div>
        </div>
      </div>
    </div>
  </master-layout>
</template>

<script>
import { mapState } from 'vuex';
import UpdatesCard from '../../components/UpdatesPage/UpdatesCard';
import LoadingSpinner from '../../components/common/LoadingSpinner.vue';
import BaseSwiper from '../../components/design/BaseSwiper.vue';
import BaseButton from '../../components/design/BaseButton.vue';
import { LIBRARY } from '@/utils/swiper-presets';
import BackButton from '@/components/common/BackButton.vue';
import { openUrl } from '@/utils/browser-helper';
import { trackButtonClick } from '@/utils/platform-check';
import { trackUpdateFilterApplied, trackUpdateViewed } from '@/utils/analytics';

// Debounce helper function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default {
  name: 'UpdatesPage',
  components: { UpdatesCard, LoadingSpinner, BaseSwiper, BaseButton, BackButton },
  data() {
    return {
      swiperOptions: LIBRARY,
    };
  },
  computed: {
    ...mapState('updates', [
      'updates',
      'earlierUpdates',
      'todaysUpdates',
      'updateTypes',
      'activeButton',
      'unreadCountsByType',
      'isLoading',
    ]),
  },
  created() {
    // Debounce only for API calls, not local filtering
    this.debouncedApiFetch = debounce(this._fetchFromApi, 300);
  },
  async ionViewWillEnter() {
    // Only fetch if we don't have data yet (homepage prefetches on navigate)
    if (!this.updates || this.updates.length === 0) {
      await this.$store.dispatch('updates/getUpdates', 'all');
    }
    // Use local filtering for initial display to avoid flash (no API call)
    this.$store.dispatch('updates/filterUpdatesLocally', this.activeButton);
    const swiper = this.$refs['update-type-swiper'];
    if (swiper) swiper.update();
  },
  updated() {
    this.$nextTick(() => {
      if (this.$route.hash) {
        const target = document.querySelector(this.$route.hash);
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  },
  methods: {
    getUnreadCountDisplay(type) {
      const count = this.unreadCountsByType[type] || 0;
      return count > 0 ? `(${count})` : '';
    },
    async filterUpdates(type) {
      this.debouncedApiFetch(type);
      if (type) trackButtonClick(`header-${type}`, 'UpdatesPage');
      trackUpdateFilterApplied({
        filter: type,
        source: 'updates-filter',
      });
    },
    async _fetchFromApi(type) {
      // Fetch fresh data from API
      await this.$store.dispatch('updates/getUpdates', type).catch(() => {
        // Silent fail - error handling is done in the store
      });
    },
    getUpdateLinkSlug(link) {
      if (typeof link !== 'string') return undefined;

      try {
        const url = new URL(link, window.location.origin);
        const segments = url.pathname.split('/').filter(Boolean);
        return segments[segments.length - 1];
      } catch (_) {
        return undefined;
      }
    },
    getUpdateAnalyticsSlug(update, isGivingNotification) {
      if (!update) return undefined;
      if (isGivingNotification) return update.slug || update.dataEventTracking;

      return (
        update.slug ||
        update.dataEventTracking ||
        this.getUpdateLinkSlug(update.path?.link || update.link)
      );
    },
    getUpdateAnalyticsId(update, isGivingNotification) {
      if (!update) return undefined;

      const directId =
        update.id || update.notification_id || update.notificationId || update.uuid || update._id;
      if (directId) return directId;
      if (isGivingNotification) return undefined;

      return this.getUpdateAnalyticsSlug(update, false);
    },
    getUpdateAnalyticsType(update) {
      return update?.type || update?.updateType?.updateType || update?.source;
    },
    activeStyle(name) {
      return this.activeButton === name
        ? 'bg-[#3C3A36] !text-white'
        : 'bg-[#EAE8E5] !text-[#3C3A36]';
    },
    async navigate(update) {
      const isGivingNotification = update.source === 'giving-api';
      const analyticsSlug = this.getUpdateAnalyticsSlug(update, isGivingNotification);

      trackUpdateViewed({
        updateId: this.getUpdateAnalyticsId(update, isGivingNotification),
        slug: analyticsSlug,
        title: !isGivingNotification ? update.title || update.dataEventTracking : undefined,
        updateType: this.getUpdateAnalyticsType(update),
        source: 'updates-page',
        redactTitle: isGivingNotification,
      });
      this.$store.dispatch('updates/markReadUpdate', update.id);
      if (update.slug) trackButtonClick(update.dataEventTracking, 'UpdatesPage');

      // Handle giving-api notifications with link from notification_recipients
      if (update.source === 'giving-api' && update.link) {
        if (this.validInternalPage(update.link)) {
          const path = update.link.replace(/^https:\/\/.*?\.heartofgodchurch\.org/, '');
          this.$router.push(this.addNotificationFlag(path));
        } else if (/^https?:\/\//.test(update.link)) {
          window.open(update.link, '_blank');
        } else {
          this.$router.push(this.addNotificationFlag(update.link));
        }
        return;
      }

      // Handle CMS notifications with path object
      const pathLink = update.path?.link;

      if (update.path?.linkType === 'inAppBrowserURL') {
        await openUrl(
          { url: pathLink },
          {
            title: update.title || update.dataEventTracking,
            source: 'updates-page',
            page: 'Updates',
          },
        );
      } else if (update.path?.linkType === 'externalURL') {
        window.open(pathLink, '_blank');
      } else if (update.path?.linkType === 'internalPage') {
        if (this.validInternalPage(pathLink)) {
          const path = pathLink.replace(/^https:\/\/.*?\.heartofgodchurch\.org/, '');
          this.$router.push(this.addNotificationFlag(path));
        } else {
          console.error('Invalid internal page link:', pathLink);
        }
      }
    },
    backToTop() {
      const elem = document.getElementById('updatesOverview');
      elem.scrollToTop(1000);
    },
    validInternalPage(path) {
      return /^https:\/\/.*?\.heartofgodchurch\.org/.test(path);
    },

    addNotificationFlag(path) {
      const TARGET_PATH_PREFIX = '/my-giving/local-transfer-egiro/status/';
      if (typeof path !== 'string') return path;

      const pathname = path.split('?')[0];
      if (!pathname.startsWith(TARGET_PATH_PREFIX)) return path;

      const url = new URL(path, window.location.origin);
      const query = Object.fromEntries(url.searchParams.entries());
      query.from = 'notifications';
      return { path: url.pathname, query };
    },
  },
};
</script>

<style scoped>
.scroll-margin {
  scroll-margin-top: 48px;
}
</style>
